import { MapPin, Mail, Phone, Smartphone, Clock, Facebook, Instagram } from "lucide-react";
import { STORE } from "@/lib/config";

export default function ContactInfo() {
  return (
    <section
      id="kontakt"
      data-testid="contact-section"
      className="relative py-24 lg:py-32 bg-neutral-950 text-white"
    >
      {/* Diagonal red accent stripe */}
      <div className="absolute top-0 left-0 right-0 h-1 bg-[#D32F2F]" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-12">
          {/* LEFT: heading */}
          <div className="lg:col-span-5">
            <div className="text-[#FCA5A5] uppercase tracking-[0.25em] text-xs font-semibold mb-4">
              Kontakt
            </div>
            <h2
              className="text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-tight"
              style={{ fontFamily: "Cabinet Grotesk" }}
            >
              Posjetite nas u
              <span className="block text-[#D32F2F]">Orašju.</span>
            </h2>
            <p className="text-white/70 mt-6 leading-relaxed text-lg">
              Naša vrata su otvorena svaki radni dan. Svratite, posavjetujte se i
              pronađite ono što tražite.
            </p>

            {/* Social */}
            <div className="mt-8 space-y-3">
              <a
                href={STORE.facebook.url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="social-facebook"
                className="group flex items-center gap-4 text-white/90 hover:text-white transition"
              >
                <span className="w-12 h-12 grid place-items-center bg-white/10 group-hover:bg-[#1877F2] rounded-full transition-colors">
                  <Facebook className="w-5 h-5" />
                </span>
                <div>
                  <div className="text-xs text-white/50">Facebook</div>
                  <div className="font-semibold">{STORE.facebook.handle}</div>
                </div>
              </a>
              <a
                href={STORE.instagram.url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="social-instagram"
                className="group flex items-center gap-4 text-white/90 hover:text-white transition"
              >
                <span className="w-12 h-12 grid place-items-center bg-white/10 group-hover:bg-gradient-to-br group-hover:from-[#F58529] group-hover:via-[#DD2A7B] group-hover:to-[#8134AF] rounded-full transition-all">
                  <Instagram className="w-5 h-5" />
                </span>
                <div>
                  <div className="text-xs text-white/50">Instagram</div>
                  <div className="font-semibold">{STORE.instagram.handle}</div>
                </div>
              </a>
            </div>
          </div>

          {/* RIGHT: details */}
          <div className="lg:col-span-7 grid sm:grid-cols-2 gap-4">
            <ContactCard
              icon={<MapPin className="w-5 h-5" />}
              label="Adresa"
              value={STORE.address}
              link={`https://maps.google.com/?q=${encodeURIComponent(STORE.address)}`}
              testid="contact-address"
            />
            <ContactCard
              icon={<Mail className="w-5 h-5" />}
              label="E-mail"
              value={STORE.email}
              link={`mailto:${STORE.email}`}
              testid="contact-email"
            />
            <ContactCard
              icon={<Phone className="w-5 h-5" />}
              label="Telefon"
              value={STORE.phone}
              link={`tel:${STORE.phone.replace(/\s/g, "")}`}
              testid="contact-phone"
            />
            <ContactCard
              icon={<Smartphone className="w-5 h-5" />}
              label="Mobitel"
              value={STORE.mobile}
              link={`tel:${STORE.mobile.replace(/\s/g, "")}`}
              testid="contact-mobile"
            />

            {/* Hours card spans both columns */}
            <div
              data-testid="working-hours"
              className="sm:col-span-2 rounded-2xl bg-white/5 backdrop-blur-md border border-white/10 p-6"
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 grid place-items-center bg-[#D32F2F] rounded-xl">
                  <Clock className="w-5 h-5" />
                </div>
                <div className="text-xs uppercase tracking-[0.2em] text-white/60 font-semibold">
                  Radno vrijeme
                </div>
              </div>
              <div className="space-y-2">
                {STORE.hours.map((h, i) => (
                  <div
                    key={i}
                    className="flex items-center justify-between py-2 border-b border-white/5 last:border-0"
                  >
                    <span className="text-white/80">{h.day}</span>
                    <span
                      className={`font-semibold ${
                        h.time === "Ne radimo" ? "text-[#FCA5A5]" : "text-white"
                      }`}
                    >
                      {h.time}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function ContactCard({ icon, label, value, link, testid }) {
  return (
    <a
      href={link}
      target={link.startsWith("http") ? "_blank" : undefined}
      rel="noopener noreferrer"
      data-testid={testid}
      className="group rounded-2xl bg-white/5 hover:bg-white/10 backdrop-blur-md border border-white/10 hover:border-[#D32F2F]/50 p-6 transition-all"
    >
      <div className="flex items-center gap-3 mb-3">
        <div className="w-10 h-10 grid place-items-center bg-[#D32F2F] rounded-xl group-hover:scale-110 transition-transform">
          {icon}
        </div>
        <div className="text-xs uppercase tracking-[0.2em] text-white/60 font-semibold">
          {label}
        </div>
      </div>
      <div className="text-white font-semibold text-lg break-all">{value}</div>
    </a>
  );
}
